import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Copy, Trash2, Lock, ArrowLeft, Upload } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import QuestionEditor from '../assessments/QuestionEditor';
import QuestionList from '../assessments/QuestionList';
import LiveScoringPanel from '../assessments/LiveScoringPanel';

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const BUILDER = () => `${API}/admin/assessments/builder`;

const STATUS_STYLE = {
  draft: 'text-gray-300 border-gray-600',
  published: 'text-emerald-300 border-emerald-700',
  locked: 'text-amber-300 border-amber-700',
};

export const AdminAssessmentBuilderTab = () => {
  const [reference, setReference] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newLocation, setNewLocation] = useState('');
  const [confirming, setConfirming] = useState(null);
  const [formKey, setFormKey] = useState(0);

  const loadList = useCallback(async () => {
    try {
      const [ref, list] = await Promise.all([
        axios.get(`${BUILDER()}/reference`, { headers: headers() }),
        axios.get(`${BUILDER()}/assessments`, { headers: headers() }),
      ]);
      setReference(ref.data);
      setAssessments(list.data.assessments);
      setNewLocation((prev) => prev || ref.data.locations[0]?.id || '');
    } catch {
      toast.error('Could not load the assessment builder');
    }
  }, []);

  const loadDetail = useCallback(async (id) => {
    try {
      const res = await axios.get(`${BUILDER()}/assessments/${id}`, { headers: headers() });
      setDetail(res.data);
    } catch {
      toast.error('Could not load that assessment');
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { if (openId) loadDetail(openId); }, [openId, loadDetail]);

  if (!reference) return null;

  const dimensionNames = reference.dimensions.reduce(
    (acc, d) => ({ ...acc, [d.key]: d.name }), {},
  );
  const locationLabel = (id) =>
    reference.locations.find((l) => l.id === id)?.label || id;

  const create = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${BUILDER()}/assessments`,
        { assessment_location_id: newLocation, instructions: '' }, { headers: headers() });
      await loadList();
      setOpenId(res.data.assessment.id);
      toast.success('Assessment created — now add its questions');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create the assessment');
    } finally {
      setBusy(false);
    }
  };

  const act = async (fn, successMessage, { refreshDetail = true } = {}) => {
    setBusy(true);
    try {
      const res = await fn();
      if (successMessage) toast.success(successMessage);
      await loadList();
      if (openId && refreshDetail) await loadDetail(openId);
      return res;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'That did not work');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const saveQuestion = async (payload) => {
    const url = editingQuestion
      ? `${BUILDER()}/questions/${editingQuestion.id}`
      : `${BUILDER()}/assessments/${openId}/questions`;
    const result = await act(
      () => (editingQuestion
        ? axios.put(url, payload, { headers: headers() })
        : axios.post(url, payload, { headers: headers() })),
      editingQuestion ? 'Question updated' : 'Question added',
    );
    if (result) {
      setEditingQuestion(null);
      setFormKey((k) => k + 1);
    }
    return Boolean(result);
  };

  const moveQuestion = async (index, direction) => {
    const ids = detail.questions.map((q) => q.id);
    const target = index + direction;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    await act(() => axios.put(`${BUILDER()}/assessments/${openId}/questions/reorder`,
      { question_ids: ids }, { headers: headers() }));
  };

  // ── list view ──
  if (!openId || !detail) {
    return (
      <div className="space-y-6" data-testid="assessment-builder-tab">
        <div>
          <h2 className="text-2xl font-bold text-white">Assessment Builder</h2>
          <p className="text-sm text-gray-400 mt-1 max-w-3xl">
            Build the framework for each assessment — where it sits in the program, its questions,
            and the weight carried by every answer. Thresholds, dimension weights and
            interpretation text live in Assessment Scoring.
          </p>
        </div>

        <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-5">
          <h3 className="text-base font-semibold text-white">Create an assessment</h3>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <div className="min-w-64">
              <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1.5">
                Where it sits
              </label>
              <select
                value={newLocation}
                onChange={(e) => setNewLocation(e.target.value)}
                data-testid="new-assessment-location-select"
                className="w-full bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-sm
                           text-white focus:outline-none focus:border-cyan-400"
              >
                {reference.locations.map((l) => (
                  <option key={l.id} value={l.id}>{l.label}</option>
                ))}
              </select>
            </div>
            <Button onClick={create} disabled={busy} data-testid="create-assessment-btn"
                    className="bg-cyan-600 hover:bg-cyan-700 text-white">
              <Plus className="w-4 h-4 mr-1.5" /> Create
            </Button>
          </div>
        </div>

        <div className="space-y-2" data-testid="assessment-list">
          {assessments.length === 0 && (
            <p className="text-sm text-gray-500" data-testid="assessment-list-empty">
              No assessments yet.
            </p>
          )}
          {assessments.map((a) => (
            <button
              key={a.id}
              onClick={() => setOpenId(a.id)}
              data-testid={`assessment-row-${a.id}`}
              className="w-full text-left bg-gray-900/60 border border-gray-700 rounded-lg px-4 py-3
                         flex items-center gap-3 hover:border-cyan-600 transition-colors"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm text-white">
                  {locationLabel(a.assessment_location_id)}
                  <span className="text-gray-500"> · v{a.version}</span>
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {a.question_count} of {reference.question_count} questions
                  {a.has_attempts ? ' · taken by participants' : ''}
                </p>
              </div>
              <Badge variant="outline" className={STATUS_STYLE[a.status] || 'text-gray-300'}>
                {a.has_attempts && <Lock className="w-3 h-3 mr-1" />}
                {a.status}
              </Badge>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ── detail view ──
  const { assessment, questions, preview, editable } = detail;

  return (
    <div className="space-y-5" data-testid="assessment-builder-detail">
      <button
        onClick={() => { setOpenId(null); setDetail(null); setEditingQuestion(null); }}
        data-testid="assessment-back-btn"
        className="text-sm text-gray-400 hover:text-gray-200 flex items-center gap-1.5
                   transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> All assessments
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-white">
            {locationLabel(assessment.assessment_location_id)}
          </h2>
          <p className="text-sm text-gray-400 mt-1">
            Version {assessment.version} · {assessment.status}
            {!editable && ' · frozen because participants have taken it'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {assessment.status !== 'published' && (
            <Button
              onClick={() => act(() => axios.post(
                `${BUILDER()}/assessments/${openId}/publish`, {}, { headers: headers() }),
                'Published')}
              disabled={busy || !preview.ready_to_publish}
              data-testid="assessment-publish-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              <Upload className="w-4 h-4 mr-1.5" /> Publish
            </Button>
          )}
          {assessment.status === 'published' && editable && (
            <Button
              onClick={() => act(() => axios.post(
                `${BUILDER()}/assessments/${openId}/unpublish`, {}, { headers: headers() }),
                'Back to draft')}
              disabled={busy}
              data-testid="assessment-unpublish-btn"
              variant="outline"
              className="border-gray-600 text-gray-300"
            >
              Unpublish
            </Button>
          )}
          <Button
            onClick={async () => {
              const res = await act(() => axios.post(
                `${BUILDER()}/assessments/${openId}/duplicate`, {}, { headers: headers() }),
                'Duplicated');
              if (res) setOpenId(res.data.assessment.id);
            }}
            disabled={busy}
            data-testid="assessment-duplicate-btn"
            variant="outline"
            className="border-gray-600 text-gray-300"
          >
            <Copy className="w-4 h-4 mr-1.5" /> Duplicate
          </Button>
          {editable && (
            <Button
              onClick={() => setConfirming({
                kind: 'assessment',
                title: 'Delete this assessment?',
                body: `${locationLabel(assessment.assessment_location_id)} v${assessment.version} `
                  + `and its ${questions.length} question`
                  + `${questions.length === 1 ? '' : 's'} will be removed. This cannot be undone.`,
                run: async () => {
                  const res = await act(
                    () => axios.delete(`${BUILDER()}/assessments/${openId}`, { headers: headers() }),
                    'Deleted',
                    { refreshDetail: false },
                  );
                  if (res) { setOpenId(null); setDetail(null); }
                },
              })}
              disabled={busy}
              data-testid="assessment-delete-btn"
              variant="outline"
              className="border-red-900 text-red-300 hover:bg-red-950"
            >
              <Trash2 className="w-4 h-4 mr-1.5" /> Delete
            </Button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-5">
            <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1.5">
              Instructions to participants
            </label>
            <textarea
              value={assessment.instructions || ''}
              onChange={(e) => setDetail({
                ...detail, assessment: { ...assessment, instructions: e.target.value },
              })}
              onBlur={() => editable && axios.put(`${BUILDER()}/assessments/${openId}`,
                { instructions: assessment.instructions }, { headers: headers() })
                .catch(() => toast.error('Could not save the instructions'))}
              rows={3}
              readOnly={!editable}
              data-testid="assessment-instructions-input"
              placeholder="Shown at the top of the assessment"
              className="w-full bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-sm
                         text-white focus:outline-none focus:border-cyan-400"
            />
          </div>

          <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-5">
            <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1.5">
              Module narrative — your framing, shown with the participant's results
            </label>
            <textarea
              value={assessment.participant_narrative || ''}
              onChange={(e) => setDetail({
                ...detail, assessment: { ...assessment, participant_narrative: e.target.value },
              })}
              onBlur={() => axios.put(`${BUILDER()}/assessments/${openId}/narrative`,
                { participant_narrative: assessment.participant_narrative || '' },
                { headers: headers() })
                .then(() => toast.success('Narrative saved'))
                .catch(() => toast.error('Could not save the narrative'))}
              rows={4}
              data-testid="assessment-narrative-input"
              placeholder="e.g. This module explored how your body and energy systems integrate with daily habits. The focus was on embodiment, stability and awareness of physical signals."
              className="w-full bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-sm
                         text-white focus:outline-none focus:border-cyan-400"
            />
            <p className="text-xs text-gray-500 mt-2">
              Optional. Editable at any time, even after members have taken this assessment — it is
              framing, not scoring. Leave it empty and the results screen simply omits it.
            </p>
          </div>

          <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-5">
            <h3 className="text-base font-semibold text-white mb-3">
              Questions
              <span className="text-sm text-gray-500 font-normal ml-2">
                {questions.length} of {reference.question_count}
              </span>
            </h3>
            <QuestionList
              questions={questions}
              dimensionNames={dimensionNames}
              editable={editable}
              onEdit={setEditingQuestion}
              onDelete={(q) => setConfirming({
                kind: 'question',
                title: `Delete question ${q.order}?`,
                body: `"${q.text}" will be removed and the remaining questions renumbered.`,
                run: () => act(
                  () => axios.delete(`${BUILDER()}/questions/${q.id}`, { headers: headers() }),
                  'Question deleted',
                ),
              })}
              onMove={moveQuestion}
            />
          </div>

          {editable && (
            <QuestionEditor
              key={editingQuestion ? `edit-${editingQuestion.id}` : `add-${formKey}`}
              reference={reference}
              editing={editingQuestion}
              onSubmit={saveQuestion}
              onCancel={() => { setEditingQuestion(null); setFormKey((k) => k + 1); }}
              saving={busy}
              disabled={!editingQuestion && questions.length >= reference.question_count}
            />
          )}
          {editable && !editingQuestion && questions.length >= reference.question_count && (
            <p className="text-sm text-amber-300" data-testid="question-limit-notice">
              All {reference.question_count} questions are entered. Delete one to add another.
            </p>
          )}
        </div>

        <LiveScoringPanel preview={preview} dimensionNames={dimensionNames} />
      </div>

      <AlertDialog open={Boolean(confirming)} onOpenChange={(open) => !open && setConfirming(null)}>
        <AlertDialogContent className="bg-gray-900 border-gray-700"
                            data-testid="builder-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">{confirming?.title}</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              {confirming?.body}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="builder-confirm-cancel"
                               className="bg-gray-800 border-gray-600 text-gray-200">
              Keep it
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="builder-confirm-delete"
              className="bg-red-700 hover:bg-red-800 text-white"
              onClick={async () => {
                const run = confirming?.run;
                setConfirming(null);
                if (run) await run();
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminAssessmentBuilderTab;
